import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, Loader2, Zap, History, Info, TrendingUp, Wifi, Bell } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import Navbar from "@/components/Navbar";
import CandlestickChart from "@/components/CandlestickChart";
import SignalCard from "@/components/SignalCard";
import MartingaleTracker from "@/components/MartingaleTracker";
import PricingModal from "@/components/PricingModal";
import SubscriptionCard from "@/components/SubscriptionCard";

const TFS = [2, 3, 5, 10, 15];

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [symbol, setSymbol] = useState(null);
  const [tf, setTf] = useState(5);
  const [chart, setChart] = useState(null);
  const [signal, setSignal] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanText, setScanText] = useState("");
  const [showPricing, setShowPricing] = useState(false);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [adminSvc, setAdminSvc] = useState(null);
  const [adminUnread, setAdminUnread] = useState(0);
  const [market, setMarket] = useState(null);
  const scanTimer = useRef(null);
  const searchingRef = useRef(false);
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const chartAssets = assets.filter((a) => a.data_source !== "pocketoption");
  const totalCredits = (user?.credits ?? 0) + (user?.sub_credits ?? 0);

  const loadHistory = useCallback(async (restoreActive = false) => {
    try {
      const { data } = await api.get("/signals");
      setHistory(data);
      if (restoreActive && data[0]?.status === "active") setSignal(data[0]);
    } catch {}
  }, []);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/signals/stats"); setStats(data); } catch {}
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [a, p, pl] = await Promise.all([api.get("/assets"), api.get("/packages"), api.get("/plans")]);
        setAssets(a.data);
        setPackages(p.data);
        setPlans(pl.data);
        const firstChartable = a.data.find((x) => x.data_source !== "pocketoption");
        if (firstChartable) setSymbol(firstChartable.symbol);
      } catch {}
    })();
    loadHistory(true);
    loadStats();
  }, [loadHistory, loadStats]);

  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    const fetchCandles = async () => {
      try {
        const { data } = await api.get(`/market/candles`, { params: { symbol, tf } });
        if (alive) setChart(data);
      } catch (e) {
        if (alive) setChart(null);
      }
    };
    fetchCandles();
    const id = setInterval(fetchCandles, 6000);
    return () => { alive = false; clearInterval(id); };
  }, [symbol, tf]);

  useEffect(() => () => { searchingRef.current = false; clearInterval(scanTimer.current); }, []);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try { const { data } = await api.get("/market/status"); if (alive) setMarket(data); } catch {}
    };
    poll();
    const id = setInterval(poll, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const allClosed = market && !market.forex_open && !market.otc_available;

  useEffect(() => {
    if (user?.role !== "admin") return;
    let alive = true;
    const poll = async () => {
      try {
        const [s, n] = await Promise.all([api.get("/admin/services"), api.get("/admin/notifications")]);
        if (alive) { setAdminSvc(s.data); setAdminUnread(n.data.unread || 0); }
      } catch {}
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  const runScanAnimation = () => {
    const names = assets.map((a) => a.display_name);
    let i = 0;
    scanTimer.current = setInterval(() => {
      setScanText(t("dash.analyzing", { name: names[i % names.length] }));
      i++;
    }, 300);
  };
  const stopScanAnimation = () => { clearInterval(scanTimer.current); setScanText(""); };

  const stopSearch = () => {
    searchingRef.current = false;
    setScanning(false);
    stopScanAnimation();
  };

  const generate = async () => {
    if (scanning) return;
    if (user?.role !== "admin" && totalCredits <= 0) {
      toast.error(t("dash.noCredits"));
      setShowPricing(true);
      return;
    }
    searchingRef.current = true;
    setScanning(true);
    setSignal(null);
    setElapsed(0);
    runScanAnimation();
    const startedAt = Date.now();
    const elapsedTimer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    try {
      while (searchingRef.current) {
        let data;
        try {
          const res = await api.post("/signals/generate");
          data = res.data;
        } catch (e) {
          if (e.response?.status === 402) {
            toast.error(t("dash.noCredits"));
            setShowPricing(true);
            break;
          }
          await sleep(8000);
          continue;
        }
        if (!searchingRef.current) break;
        if (data.market_closed) {
          toast.error(t("dash.allClosed"));
          setMarket((m) => ({ ...(m || {}), forex_open: data.forex_open, otc_available: data.otc_available, available_markets: 0 }));
          break;
        }
        if (data.found) {
          setSignal(data.signal);
          const s = data.signal;
          const chartable = assets.find((a) => a.symbol === s.asset_symbol && a.data_source !== "pocketoption");
          if (chartable) { setSymbol(s.asset_symbol); setTf(s.timeframe); }
          toast.success(t("dash.signalFound", { dir: s.direction, asset: s.asset_name, tf: s.timeframe }));
          await refresh();
          loadHistory();
          loadStats();
          break;
        }
        await sleep(12000);
      }
    } finally {
      clearInterval(elapsedTimer);
      searchingRef.current = false;
      setScanning(false);
      stopScanAnimation();
    }
  };

  const statusLabel = (s) => (s === "won" ? t("dash.statusWon") : s === "lost" ? t("dash.statusLost") : t("dash.statusActive"));

  return (
    <div className="min-h-screen bg-[#0A0D14] text-white">
      <Navbar onBuy={() => setShowPricing(true)} />
      <main className="max-w-7xl mx-auto px-4 md:px-6 pt-20 pb-12 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {user?.role === "admin" && (
            <div className="rounded-xl border border-[#243047] bg-[#121824] p-4 flex items-center justify-between flex-wrap gap-3" data-testid="admin-status-banner">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="flex items-center gap-1.5 text-xs font-mono text-[#94A3B8]">
                  <span className={`w-2 h-2 rounded-full ${adminSvc?.forex ? "bg-[#00FFC2]" : "bg-[#EC1E9C]"}`} /> Forex
                </span>
                <span className="flex items-center gap-1.5 text-xs font-mono text-[#94A3B8]" data-testid="admin-otc-status">
                  <Wifi className="w-3.5 h-3.5 text-[#00E0B0]" />
                  {t("dash.otcStatus")} {adminSvc?.otc?.active
                    ? <span className={adminSvc.otc.active.status === "connesso" ? "text-[#00FFC2]" : "text-[#EC1E9C]"}>{adminSvc.otc.active.label} ({adminSvc.otc.active.status})</span>
                    : <span className="text-[#64748B]">{t("dash.noActiveAccount")}</span>}
                </span>
              </div>
              <button onClick={() => navigate("/admin")} className="relative flex items-center gap-1.5 text-xs font-semibold text-[#00FFC2] hover:brightness-110" data-testid="admin-notifications-badge">
                <Bell className="w-4 h-4" /> {t("dash.notifications")}
                {adminUnread > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#EC1E9C] text-white text-[10px] font-bold">{adminUnread}</span>}
              </button>
            </div>
          )}

          {market && (!market.forex_open || !market.otc_available) && (
            <div data-testid="market-status-banner"
              className={`rounded-xl border px-4 py-3 flex items-start gap-2 text-sm ${allClosed ? "border-[#EC1E9C]/60 bg-[#EC1E9C]/10 text-white" : "border-[#243047] bg-[#121824] text-[#94A3B8]"}`}>
              <Info className={`w-4 h-4 mt-0.5 shrink-0 ${allClosed ? "text-[#EC1E9C]" : "text-[#00FFC2]"}`} />
              <span>{allClosed ? t("dash.allClosed") : !market.otc_available ? t("dash.otcDown") : t("dash.forexClosed")}</span>
            </div>
          )}

          <div className={`rounded-xl border bg-[#121824] p-5 ${scanning ? "border-[#EC1E9C] scanning-glow" : "border-[#243047]"}`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                  <Radar className={`w-5 h-5 text-[#00FFC2] ${scanning ? "animate-spin" : ""}`} /> {t("dash.scanner")}
                </h2>
                <p className="text-sm text-[#94A3B8] mt-1" data-testid="scanner-markets-count">
                  {t("dash.scanOn")} <span className="font-mono text-white">{market ? market.available_markets : assets.length}</span> {t("dash.markets")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {scanning && (
                  <button onClick={stopSearch} data-testid="stop-signal-button"
                    className="px-4 py-3 rounded-xl border border-[#243047] text-[#94A3B8] hover:text-[#EC1E9C] font-semibold transition">
                    {t("dash.stop")}
                  </button>
                )}
                <button onClick={generate} disabled={scanning || allClosed} data-testid="generate-signal-button"
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold hover:brightness-110 transition disabled:opacity-70">
                  {scanning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
                  {scanning ? t("dash.searching") : t("dash.generate")}
                </button>
              </div>
            </div>
            {scanning && (
              <div data-testid="scanner-status-indicator" className="mt-4 space-y-1">
                <div className="font-mono text-sm text-[#EC1E9C] flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#EC1E9C] animate-pulse" /> {scanText || t("dash.starting")}
                  <span className="text-[#64748B]">· {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}</span>
                </div>
                <p className="text-xs text-[#64748B]">
                  {t("dash.bgNote1")} <span className="text-white">{t("dash.bgNote2")}</span>{t("dash.bgNote3")}
                </p>
              </div>
            )}
          </div>

          {signal && (
            <div className="space-y-4">
              <SignalCard signal={signal} />
              {signal.data_source === "pocketoption" && (
                <div className="flex items-start gap-2 text-xs text-[#94A3B8] bg-[#0A0D14] border border-[#243047] rounded-lg px-3 py-2">
                  <Info className="w-4 h-4 text-[#00E0B0] mt-0.5" />
                  {t("dash.otcNote")}
                </div>
              )}
            </div>
          )}

          <div className="rounded-xl border border-[#243047] bg-[#121824] overflow-hidden" data-testid="candlestick-chart-container">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#243047] flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#00FFC2]" />
                <select value={symbol || ""} onChange={(e) => setSymbol(e.target.value)} data-testid="chart-asset-select"
                  className="bg-[#0A0D14] border border-[#243047] rounded-lg px-3 py-1.5 text-sm font-semibold text-white outline-none">
                  {chartAssets.map((a) => (
                    <option key={a.symbol} value={a.symbol}>{a.display_name} · {a.payout}%</option>
                  ))}
                </select>
                <span className="text-[10px] font-mono uppercase text-[#64748B] border border-[#243047] rounded px-1.5 py-0.5">{t("dash.forexLive")}</span>
              </div>
              <div className="flex gap-1">
                {TFS.map((x) => (
                  <button key={x} onClick={() => setTf(x)} data-testid={`timeframe-select-${x}m`}
                    className={`px-3 py-1.5 rounded-md text-xs font-mono font-bold transition ${
                      tf === x ? "bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B]" : "text-[#94A3B8] hover:text-white border border-[#243047]"
                    }`}>
                    {x}m
                  </button>
                ))}
              </div>
            </div>
            <div className="p-2">
              <CandlestickChart candles={chart?.candles || []} />
            </div>
            <div className="px-5 py-2 border-t border-[#243047] flex items-center gap-4 text-[10px] font-mono text-[#64748B]">
              <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-[#00E0B0]" /> {t("dash.bollinger")}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-[#EC1E9C]" /> {t("dash.sma")}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#00FFC2] rounded-sm" /> {t("dash.up")}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#EC1E9C] rounded-sm" /> {t("dash.down")}</span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {signal ? (
            <MartingaleTracker signal={signal} onUpdated={(s) => { setSignal(s); loadStats(); loadHistory(); }} onWin={generate} />
          ) : (
            <div className="rounded-xl border border-[#243047] bg-[#121824] p-6 text-center">
              <Radar className="w-8 h-8 text-[#243047] mx-auto" />
              <p className="text-sm text-[#94A3B8] mt-3">{t("dash.pressGenerate1")} <span className="text-white font-semibold">{t("dash.generate")}</span> {t("dash.pressGenerate2")}</p>
            </div>
          )}

          <SubscriptionCard onViewPlans={() => setShowPricing(true)} />

          <div className="rounded-xl border border-[#243047] bg-[#121824] p-5" data-testid="performance-stats">
            <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-[#00FFC2]" /> {t("dash.performance")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <StatBox label={t("dash.winRate")} value={`${stats?.win_rate ?? 0}%`} color="#00FFC2" testid="stat-winrate" />
              <StatBox label={t("dash.total")} value={stats?.total ?? 0} color="#F8FAFC" testid="stat-total" />
              <StatBox label={t("dash.won")} value={stats?.won ?? 0} color="#00FFC2" testid="stat-won" />
              <StatBox label={t("dash.lost")} value={stats?.lost ?? 0} color="#EC1E9C" testid="stat-lost" />
            </div>
          </div>

          <div className="rounded-xl border border-[#243047] bg-[#121824] p-5">
            <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><History className="w-4 h-4 text-[#00E0B0]" /> {t("dash.history")}</h3>
            <div className="space-y-2 max-h-[420px] overflow-auto" data-testid="signal-history">
              {history.length === 0 && <p className="text-sm text-[#64748B]">{t("dash.noHistory")}</p>}
              {history.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-lg bg-[#0A0D14] border border-[#243047] px-3 py-2">
                  <div>
                    <div className="text-sm font-semibold text-white">{s.asset_name}</div>
                    <div className="text-[11px] font-mono text-[#64748B]">{s.timeframe}m · {s.candle_count} {t("dash.candles")}</div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-bold font-mono ${s.direction === "CALL" ? "text-[#00FFC2]" : "text-[#EC1E9C]"}`}>{s.direction}</span>
                    <div className={`text-[10px] font-mono uppercase ${s.status === "won" ? "text-[#00FFC2]" : "text-[#EC1E9C]"}`}>{statusLabel(s.status)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <PricingModal open={showPricing} onClose={() => setShowPricing(false)} packages={packages} plans={plans} />
    </div>
  );
}

function StatBox({ label, value, color, testid }) {
  return (
    <div className="rounded-lg bg-[#0A0D14] border border-[#243047] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#64748B]">{label}</div>
      <div className="font-mono font-bold text-xl mt-0.5" style={{ color }} data-testid={testid}>{value}</div>
    </div>
  );
}
